/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/UI/Button';
import { Card, CardContent } from '@/components/UI/Card';
import { Switch } from '@/components/UI/Switch';
import { Label } from '@/components/UI/Label';
import { X } from 'lucide-react';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

interface CookieConsentBannerProps {
  onConsentChange: (analytics: boolean, advertising: boolean) => void;
}

/* -------------------------------------------------------------------------- */
/*                                 Component                                  */
/* -------------------------------------------------------------------------- */

export default function CookieConsentBanner({ onConsentChange }: CookieConsentBannerProps) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [advertisingConsent, setAdvertisingConsent] = useState(false);

  useEffect(() => {
    // Vérifier si le consentement a déjà été donné
    const savedConsent = localStorage.getItem('cookie-consent');
    if (!savedConsent) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    setAnalyticsConsent(true);
    setAdvertisingConsent(true);
    handleConsentSubmit(true, true);
  };

  const handleRejectAll = () => {
    setAnalyticsConsent(false);
    setAdvertisingConsent(false);
    handleConsentSubmit(false, false);
  };

  const handleSavePreferences = () => {
    handleConsentSubmit(analyticsConsent, advertisingConsent);
  };

  const handleConsentSubmit = (analytics: boolean, advertising: boolean) => {
    // Sauvegarder le consentement
    const consentData = {
      analytics,
      advertising,
      timestamp: Date.now(),
      version: '1.0'
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consentData));
    
    // Notifier le parent
    onConsentChange(analytics, advertising);
    
    // Masquer la bannière
    setIsVisible(false);
  };

  const handleClose = () => {
    // Si l'utilisateur ferme sans choisir, considérer comme refus
    handleConsentSubmit(false, false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-black/50">
      <Card className="max-w-4xl mx-auto">
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('cookie_consent.title')}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            {t('cookie_consent.description')}
          </p>

          <div className="space-y-4 mb-6">
            {/* Analytics Cookies */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="analytics" className="text-sm font-medium">
                  {t('cookie_consent.analytics.title')}
                </Label>
                <p className="text-xs text-gray-500 mt-1">
                  {t('cookie_consent.analytics.description')}
                </p>
              </div>
              <Switch
                id="analytics"
                checked={analyticsConsent}
                onCheckedChange={setAnalyticsConsent}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <Button
              variant="outline"
              onClick={handleRejectAll}
              className="w-full sm:w-auto"
            >
              {t('cookie_consent.reject_all')}
            </Button>
            <Button
              variant="outline"
              onClick={handleSavePreferences}
              className="w-full sm:w-auto"
            >
              {t('cookie_consent.save_preferences')}
            </Button>
            <Button
              onClick={handleAcceptAll}
              className="w-full sm:w-auto"
            >
              {t('cookie_consent.accept_all')}
            </Button>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            {t('cookie_consent.legal_notice')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
